import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { useAuthStore } from '../stores/authStore';

export interface BusinessSchedulePreferences {
  business_id: string;
  walk_in_enabled: boolean;
  lease_end_markers_enabled: boolean;
  drop_off_windows_enabled: boolean;
}

export interface UseBusinessSchedulePreferencesResult {
  data: BusinessSchedulePreferences | null;
  loading: boolean;
  error: string | null;
}

export function useBusinessSchedulePreferences(): UseBusinessSchedulePreferencesResult {
  const businessId = useAuthStore((s) => s.business?.id);
  const [data, setData] = useState<BusinessSchedulePreferences | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    if (!businessId) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- guard branch for missing business id; clears stale hook state without creating update loops
      setData(null);
      setLoading(false);
      return () => {
        cancelled = true;
      };
    }

    setLoading(true);
    setError(null);

    void (async () => {
      const { data: row, error: queryError } = await supabase
        .from('business_schedule_preferences')
        .select('business_id, walk_in_enabled, lease_end_markers_enabled, drop_off_windows_enabled')
        .eq('business_id', businessId)
        .maybeSingle();

      if (cancelled) return;

      if (queryError) {
        setError(queryError.message);
        setData(null);
      } else {
        setData((row as BusinessSchedulePreferences) ?? null);
      }
      setLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [businessId]);

  return { data, loading, error };
}
